import { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LogOut, Package, ArrowLeft, FolderKanban, Check, Shield, StickyNote, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth, api } from '../App';
import { ASSETS } from '../constants/site';
import { ProjectFiles } from './admin/AdminProjects';

const STATUS_LABELS = { in_progress: 'IN PROGRESS', review: 'IN REVIEW', done: 'DELIVERED' };
const STATUS_CLS = {
  in_progress: 'text-sky-300 border-sky-500/40',
  review: 'text-amber-300 border-amber-500/40',
  done: 'text-emerald-300 border-emerald-500/40',
};

function ProjectCard({ p, isCustomer }) {
  const doneCount = (p.milestones || []).filter((m) => m.done).length;
  const total = (p.milestones || []).length;

  return (
    <div data-testid="project-card" className="border border-slate-800 rounded-2xl bg-[#0F1C33]/60 p-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <h3 className="font-display font-semibold text-lg">{p.title}</h3>
        <span className={`font-mono-tech text-[10px] tracking-widest border rounded-full px-4 py-1.5 ${STATUS_CLS[p.status] || ''}`}>
          {STATUS_LABELS[p.status] || p.status}
        </span>
      </div>
      {total > 0 && (
        <div className="mb-4">
          <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#D4AF37] to-[#F3C64F] transition-all duration-700" style={{ width: `${(doneCount / total) * 100}%` }} />
          </div>
          <p className="font-mono-tech text-[10px] tracking-widest text-slate-500 mt-2">{doneCount} / {total} MILESTONES</p>
        </div>
      )}
      <ul className="space-y-2">
        {(p.milestones || []).map((m, i) => (
          <li key={i} className="flex items-center gap-3 text-sm">
            <span className={`h-5 w-5 rounded border flex items-center justify-center ${m.done ? 'border-amber-400 bg-amber-500/20' : 'border-slate-700'}`}>
              {m.done && <Check size={12} className="text-amber-300" />}
            </span>
            <span className={m.done ? 'text-slate-500 line-through' : 'text-slate-300'}>{m.label}</span>
          </li>
        ))}
      </ul>
      {p.dev_note && (
        <div data-testid="project-dev-note" className="mt-4 border-l-2 border-sky-500/40 pl-3">
          <p className="font-mono-tech text-[9px] tracking-widest text-sky-300/80 mb-1 flex items-center gap-1.5">
            <StickyNote size={11} /> NOTE FROM THE TEAM
          </p>
          <p className="text-xs text-slate-400 leading-relaxed">{p.dev_note}</p>
        </div>
      )}
      {isCustomer && <ProjectFiles projectId={p.id} />}
    </div>
  );
}

function SecuritySection({ hasPassword }) {
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.next !== form.confirm) {
      toast.error('New passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await api.post('/auth/set-password', { password: form.next, current_password: form.current });
      toast.success(hasPassword ? 'Password updated.' : 'Password set — you can now sign in with email & password too.');
      setForm({ current: '', next: '', confirm: '' });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not update the password.');
    } finally {
      setBusy(false);
    }
  };

  const inputCls = 'w-full bg-[#0B1528]/70 border border-slate-700/60 focus:border-amber-500/60 rounded-xl px-4 py-2.5 text-sm outline-none transition-colors placeholder:text-slate-600';

  return (
    <div className="mb-12">
      <h2 className="font-mono-tech text-xs tracking-[0.3em] text-amber-400/90 uppercase mb-5 flex items-center gap-2">
        <Shield size={14} /> Sign-in & Security
      </h2>
      <form onSubmit={submit} className="border border-slate-800 rounded-2xl bg-[#0F1C33]/60 p-6 space-y-3">
        <p className="text-xs text-slate-500 mb-1">
          {hasPassword
            ? 'Your account works with Google and password sign-in. Change your password below.'
            : 'You signed up with Google. Set a password to also sign in with email & password — same account, both methods.'}
        </p>
        {hasPassword && (
          <input data-testid="current-password-input" type="password" required placeholder="Current password" value={form.current}
            onChange={(e) => setForm({ ...form, current: e.target.value })} className={inputCls} />
        )}
        <div className="grid sm:grid-cols-2 gap-3">
          <input data-testid="new-password-input" type="password" required minLength={8} placeholder="New password (8+ chars)" value={form.next}
            onChange={(e) => setForm({ ...form, next: e.target.value })} className={inputCls} />
          <input data-testid="confirm-password-input" type="password" required minLength={8} placeholder="Confirm new password" value={form.confirm}
            onChange={(e) => setForm({ ...form, confirm: e.target.value })} className={inputCls} />
        </div>
        <button data-testid="set-password-button" type="submit" disabled={busy}
          className="inline-flex items-center gap-2 border border-amber-500/40 text-amber-300 rounded-full px-6 py-2.5 text-sm hover:bg-amber-500/10 transition-colors disabled:opacity-50">
          {busy && <Loader2 size={14} className="animate-spin" />}
          {hasPassword ? 'Update password' : 'Link password sign-in'}
        </button>
      </form>
    </div>
  );
}

export default function Account() {
  const location = useLocation();
  const { user, loading, login, logout } = useAuth();
  const [payments, setPayments] = useState([]);
  const [projects, setProjects] = useState([]);
  const effectiveUser = user || location.state?.user;
  const role = effectiveUser?.role || (effectiveUser?.is_owner ? 'admin' : 'customer');
  const isAdmin = role === 'admin';
  const isStaff = isAdmin || role === 'developer';

  useEffect(() => {
    if (effectiveUser) {
      api.get('/payments/my').then((r) => setPayments(r.data)).catch(() => {});
      api.get('/projects').then((r) => setProjects(r.data)).catch(() => {});
    }
  }, [effectiveUser]);

  if (loading && !effectiveUser) {
    return (
      <div className="min-h-screen bg-[#050B14] flex items-center justify-center">
        <div className="font-mono-tech text-amber-400/80 text-sm tracking-widest animate-pulse">VERIFYING SESSION…</div>
      </div>
    );
  }

  if (!effectiveUser) {
    return (
      <div className="min-h-screen bg-[#050B14] flex items-center justify-center px-6">
        <div className="gold-ring rounded-3xl bg-[#0F1C33]/70 p-10 max-w-md w-full text-center">
          <img src={ASSETS.icon} alt="GSLAB" className="h-16 w-16 rounded-xl mx-auto mb-6" />
          <h1 className="font-display font-bold text-2xl mb-3">Client Portal</h1>
          <p className="text-sm text-slate-400 mb-8">Sign in with your Google account to view your GSLAB projects and active plans.</p>
          <button data-testid="account-login-button" onClick={login}
            className="w-full bg-gradient-to-r from-[#D4AF37] via-[#F3C64F] to-[#D4AF37] text-[#050B14] font-bold rounded-full px-6 py-3.5">
            Sign in with Google
          </button>
          <a href="/login" className="block text-xs text-slate-400 hover:text-amber-300 mt-4 transition-colors">
            or sign in with email &amp; password
          </a>
          <a href="/" className="inline-flex items-center gap-2 text-xs text-slate-500 hover:text-amber-300 mt-6 transition-colors">
            <ArrowLeft size={13} /> Back to gslab.engineer
          </a>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="account-dashboard" className="min-h-screen bg-[#050B14] pt-28 pb-20 px-4 sm:px-8">
      <div className="max-w-4xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-4">
            {effectiveUser.picture ? (
              <img src={effectiveUser.picture} alt="" className="h-14 w-14 rounded-full border border-amber-500/40" />
            ) : (
              <span className="h-14 w-14 rounded-full bg-amber-500/20 border border-amber-500/40 flex items-center justify-center text-amber-300 font-bold text-xl">
                {effectiveUser.name?.[0] || 'U'}
              </span>
            )}
            <div>
              <h1 className="font-display font-bold text-2xl">{effectiveUser.name}</h1>
              <p className="text-sm text-slate-500">
                {effectiveUser.email} · <span className="text-amber-300 uppercase font-mono-tech text-[10px] tracking-widest">{role}</span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {isStaff && (
              <Link to={isAdmin ? '/admin' : '/admin/projects'} data-testid="account-admin-link" className="inline-flex items-center gap-2 text-sm text-amber-300 border border-amber-500/40 rounded-full px-4 py-2 hover:bg-amber-500/10 transition-colors">
                <Shield size={14} /> Staff Area
              </Link>
            )}
            <a href="/" className="text-sm text-slate-400 hover:text-amber-300 transition-colors">← Site</a>
            <button data-testid="account-logout-button" onClick={logout} className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-red-400 border border-slate-700 rounded-full px-4 py-2 transition-colors">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </motion.div>

        <h2 className="font-mono-tech text-xs tracking-[0.3em] text-amber-400/90 uppercase mb-5 flex items-center gap-2">
          <Package size={14} /> Your Plans & Purchases
        </h2>
        {payments.length === 0 ? (
          <div className="border border-slate-800 rounded-2xl bg-[#0F1C33]/60 p-10 text-center mb-12">
            <p className="text-sm text-slate-400 mb-2">No active plans yet. Pick a build or care plan to get your engine started.</p>
            <p className="text-xs text-slate-500 mb-6">Once your order is confirmed, your project appears here and you can upload your logo, designs and requirement documents straight into it.</p>
            <a href="/#pricing" data-testid="account-view-pricing-link" className="inline-block bg-gradient-to-r from-[#D4AF37] to-[#F3C64F] text-[#050B14] font-bold rounded-full px-7 py-3 text-sm">
              View Pricing
            </a>
          </div>
        ) : (
          <div className="space-y-3 mb-12">
            {payments.map((p) => (
              <div key={p.session_id} className="flex items-center justify-between border border-slate-800 rounded-2xl bg-[#0F1C33]/60 px-6 py-5">
                <div>
                  <div className="font-display font-semibold">{p.package_name}</div>
                  <div className="font-mono-tech text-[10px] tracking-widest text-slate-500 mt-1 uppercase">
                    {p.kind === 'subscription' ? 'MONTHLY PLAN' : 'ONE-TIME'} · ${Number(p.amount).toLocaleString()}
                  </div>
                </div>
                <span className="font-mono-tech text-[10px] tracking-widest text-emerald-300 border border-emerald-500/40 rounded-full px-4 py-1.5">ACTIVE</span>
              </div>
            ))}
          </div>
        )}

        <h2 className="font-mono-tech text-xs tracking-[0.3em] text-amber-400/90 uppercase mb-5 flex items-center gap-2">
          <FolderKanban size={14} /> Your Projects
        </h2>
        {projects.length === 0 ? (
          <p className="text-sm text-slate-500 border border-slate-800 rounded-2xl bg-[#0F1C33]/60 p-6 mb-12">
            No projects yet — once your build kicks off, milestones will appear here.
          </p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4 mb-12">
            {projects.map((p) => (
              <ProjectCard key={p.id} p={p} isCustomer={!isStaff || effectiveUser?.is_customer} />
            ))}
          </div>
        )}

        <SecuritySection hasPassword={!!effectiveUser.has_password} />
      </div>
    </div>
  );
}
